import type { ComponentPropsWithRef, ReactNode } from 'react';
import { useState, useMemo } from 'react';
import { InputGroup, Select, InputField, CountryFlag } from '@kiwicom/orbit-components';
import type { TranslationType } from '@kiwicom/account-consts';
import { useIntl } from 'react-intl';

type OnChangeProps = Readonly<{
  countryCode: string;
  phoneNumber: string; // Phone numbers could start with 0 🤔
}>;
type CountriesWithCodes = {
  readonly code: string;
  readonly callingCode: string;
  readonly name: string;
}[];

type Props = Readonly<
  OnChangeProps & {
    onChange: (arg0: OnChangeProps) => void;
    countriesWithCodes: CountriesWithCodes;
    onBlur?: ComponentPropsWithRef<'input'>['onBlur'];
    label?: TranslationType;
    dataTest?: string;
    error?: ReactNode | null | undefined;
  }
>;

const PhoneInput = (props: Props): JSX.Element => {
  const intl = useIntl();
  const { countryCode, phoneNumber } = props;
  const [phone, setPhone] = useState({
    countryCode,
    phoneNumber,
  });

  const { onBlur, countriesWithCodes, label, dataTest, error } = props;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newState = { ...phone, [name]: value };
    setPhone(newState);
    props.onChange(newState);
  };

  const handleBlur = (e) => {
    if (onBlur !== undefined) {
      onBlur(e);
    }
  };

  const callPrefix = useMemo(
    () => countriesWithCodes.find((c) => c.code === phone.countryCode)?.callingCode,
    [countriesWithCodes, phone.countryCode],
  );
  const options = useMemo(
    () =>
      countriesWithCodes
        .filter((c) => c.callingCode)
        .map((c) => ({
          value: c.code,
          label: `${c.name} (${c.callingCode})`,
        })),
    [countriesWithCodes],
  );
  const placeholderTranslation = intl.formatMessage({
    id: 'account_v2.settings.traveler_modal.contact_form.prefix_placeholder',
    defaultMessage: 'Select',
  });
  // until orbit enables optional onChange on InputGroup
  return (
    <InputGroup
      flex={['0 0 126px', '0 1 100%']}
      label={label}
      dataTest={dataTest}
      error={error}
      onChange={handleChange}
      onBlur={handleBlur}
    >
      <Select
        customValueText={callPrefix}
        prefix={
          phone.countryCode !== '' && (
            <CountryFlag
              name={options.find((i) => i.value === phone.countryCode)?.label}
              code={phone.countryCode}
            />
          )
        }
        dataTest="PhoneCountryCode"
        options={options}
        name="countryCode"
        value={phone.countryCode}
        placeholder={placeholderTranslation}
      />
      <InputField
        dataTest="PhoneNumberInput"
        inputMode="tel"
        name="phoneNumber"
        value={phone.phoneNumber}
      />
    </InputGroup>
  );
};

export default PhoneInput;
